import type { Card } from "../entities/types.ts";
import type { VeraPose } from "./OperatorMascot.ts";

// ─── i18n-keyed commander quips (BL-89) ──────────────────────────────────────
//
// `quips(prefix, lines)` keys an EN/DE pool the same way as the comms barks:
// the pools below hold **i18n keys**, the pick logic + RNG are unchanged (keys
// substitute 1:1 for the old raw strings), and `commsLine` resolves the picked
// key at the display point. EN is the canonical fallback; DE is new.

const COMMS_QUIP_EN: Record<string, string> = {};
const COMMS_QUIP_DE: Record<string, string> = {};

function quips(
  prefix: string,
  lines: ReadonlyArray<readonly [en: string, de: string]>,
): readonly string[] {
  return Object.freeze(
    lines.map(([en, de], i) => {
      const key = `${prefix}.${i}`;
      COMMS_QUIP_EN[key] = en;
      COMMS_QUIP_DE[key] = de;
      return key;
    }),
  );
}

export const COMMANDER_QUIPS: Record<Card["rarity"], readonly string[]> =
  Object.freeze({
    common: quips("comms.quip.common", [
      ["BASELINE SKILL. STABLE PICK.", "BASIS-SKILL. STABILE WAHL."],
      ["ROUTINE UPGRADE. KEEP MOVING.", "ROUTINE-UPGRADE. WEITER SO."],
      [
        "STANDARD PACKAGE. STILL USEFUL.",
        "STANDARDPAKET. TROTZDEM NÜTZLICH.",
      ],
      // S193 (BL-271): drawn once per draft — grown so the screen stops echoing.
      ["CLEAN COMMON. NO SURPRISES.", "SAUBERER COMMON. KEINE ÜBERRASCHUNG."],
      ["SIMPLE TOOL. STILL A TOOL.", "SIMPLES WERKZEUG. ABER WERKZEUG."],
    ]),
    rare: quips("comms.quip.rare", [
      ["NICE PULL. THIS OPENS LINES.", "GUTER ZUG. DAS ÖFFNET BAHNEN."],
      [
        "RARE SIGNAL. WE CAN WORK WITH THIS.",
        "SELTENES SIGNAL. DAMIT GEHT WAS.",
      ],
      ["GOOD FIND. SLOT VALUE IS SOLID.", "GUTER FUND. SLOT-WERT IST SOLIDE."],
      ["RARE TRACE. NICE CATCH.", "SELTENE SPUR. GUTER FANG."],
      ["THIS EARNS ITS SLOT.", "DAS VERDIENT SEINEN SLOT."],
    ]),
    epic: quips("comms.quip.epic", [
      [
        "EPIC SPIKE. THIS CHANGES PRIORITIES.",
        "EPISCHER AUSSCHLAG. DAS ÄNDERT PRIORITÄTEN.",
      ],
      [
        "THAT IS HEAVY IMPACT. CHOOSE THE SLOT CAREFULLY.",
        "SCHWERE WIRKUNG. WÄHL DEN SLOT MIT BEDACHT.",
      ],
      [
        "BIG SWING SKILL. MAKE THE REPLACE COUNT.",
        "GROSSER SKILL. LASS DEN TAUSCH ZÄHLEN.",
      ],
      ["EPIC-GRADE. PLAN AROUND IT.", "EPIC-KLASSE. PLANE DAMIT."],
      ["SERIOUS FIREPOWER IN THAT.", "DA STECKT ECHTE FEUERKRAFT DRIN."],
    ]),
    legendary: quips("comms.quip.legendary", [
      [
        "WHOA - WE NEED THIS IN OUR SLOTS.",
        "WOAH - DAS BRAUCHEN WIR IN DEN SLOTS.",
      ],
      [
        "LEGENDARY LOCK. THIS IS A RUN DEFINER.",
        "LEGENDÄR. DAS DEFINIERT DEN RUN.",
      ],
      [
        "TOP-TIER SIGNAL. DO NOT WASTE THIS WINDOW.",
        "SPITZEN-SIGNAL. VERSCHWENDE DIESES FENSTER NICHT.",
      ],
      ["GOLD-TIER. ONCE A RUN, MAYBE.", "GOLD-KLASSE. EINMAL PRO RUN, VIELLEICHT."],
      [
        "THE LOG WILL REMEMBER THIS PULL.",
        "DAS LOG WIRD DIESEN ZUG NICHT VERGESSEN.",
      ],
    ]),
  });

export function pickCommanderQuip(
  card: Card,
  rng: () => number = Math.random,
): string {
  const pool = COMMANDER_QUIPS[card.rarity] ?? COMMANDER_QUIPS.common;
  if (pool.length === 0) return "";

  const raw = rng();
  const index = Math.min(pool.length - 1, Math.floor(raw * pool.length));
  return pool[Math.max(0, index)] ?? pool[0] ?? "";
}

// ─── BL-60: VERA as a contextual SWAP advisor ─────────────────────────────────
//
// Owner CR (2026-06-06): the swap-menu VERA was keyed by **rarity only** (3 lines
// per tier) — she judged the incoming card blind to your deck and could repeat
// the same line. This upgrades her into a real advisor: a PURE, read-only
// `swapAdvice(contender, loadout)` that reads the ACTUAL situation (rarity +
// category-coverage + your weakest slot) and gives a tip that makes sense, drawn
// from a per-case pool with no-immediate-repeat (the `BeatScheduler.pick` dedup
// pattern). It also drives her FACE: `cheer` on a go-for-it case, `alert` on a
// think-about-it case, `idle` on a neutral one — so the face, the line and the
// cards all agree. `wow` STAYS the kernel_panic-exclusive reaction (never reused
// here). Render-only / determinism-neutral: it reads the loadout UI state +
// contender, touches no sim, and the pick RNG is render-only (like
// `pickCommanderQuip`). A brittle effect-synergy graph is deliberately avoided —
// the heuristic is robust on objective rarity + category facts.

/** The six swap situations the advisor recognises. */
export type SwapCase =
  | "legendary"
  | "fills-a-gap"
  | "clear-upgrade"
  | "over-stacked"
  | "hold"
  | "generic";

const RARITY_RANK: Record<Card["rarity"], number> = {
  common: 0,
  rare: 1,
  epic: 2,
  legendary: 3,
};

/** Per-case advice pools (≥4 lines each; terse cyber-ops VERA, SKILL wording).
 *  `generic` falls through to the rarity pools (the original lines). */
export const SWAP_ADVICE: Record<
  Exclude<SwapCase, "generic">,
  readonly string[]
> = Object.freeze({
  legendary: quips("comms.swap.legendary", [
    [
      "LEGENDARY. DROP YOUR WEAKEST AND LOCK IT IN.",
      "LEGENDÄR. WIRF DEN SCHWÄCHSTEN RAUS UND NIMM ES.",
    ],
    [
      "TOP-TIER INTERCEPT. THIS REWRITES THE RUN — MAKE ROOM.",
      "SPITZEN-FANG. DAS SCHREIBT DEN RUN NEU — MACH PLATZ.",
    ],
    [
      "GOLD SIGNAL. SWAP YOUR SOFTEST SLOT, NO HESITATION.",
      "GOLD-SIGNAL. TAUSCH DEN WEICHSTEN SLOT, OHNE ZÖGERN.",
    ],
    [
      "RUN-DEFINER. SOMETHING HAS TO GO — IT'S WORTH IT.",
      "RUN-PRÄGER. ETWAS MUSS WEICHEN — ES LOHNT SICH.",
    ],
  ]),
  "clear-upgrade": quips("comms.swap.clearUpgrade", [
    [
      "THIS OUTRANKS YOUR WEAKEST SLOT. EASY TRADE.",
      "DAS SCHLÄGT DEINEN SCHWÄCHSTEN SLOT. LEICHTER TAUSCH.",
    ],
    [
      "STRAIGHT UPGRADE OVER YOUR SOFTEST CARD. TAKE IT.",
      "KLARES UPGRADE GEGEN DEINE WEICHSTE KARTE. NIMM ES.",
    ],
    [
      "HIGHER TIER THAN YOUR FLOOR. SWAP UP.",
      "HÖHERE STUFE ALS DEIN MINIMUM. TAUSCH HOCH.",
    ],
    [
      "BEATS YOUR LOWEST. CLEAN PROMOTION.",
      "SCHLÄGT DEIN SCHWÄCHSTES. SAUBERE BEFÖRDERUNG.",
    ],
  ]),
  "fills-a-gap": quips("comms.swap.fillsAGap", [
    [
      "NO COVER IN THIS LANE — THIS PATCHES THE HOLE.",
      "KEINE DECKUNG IN DIESER BAHN — DAS STOPFT DAS LOCH.",
    ],
    [
      "YOUR DECK'S BLIND HERE. THIS FILLS THE GAP.",
      "DEIN DECK IST HIER BLIND. DAS FÜLLT DIE LÜCKE.",
    ],
    [
      "MISSING CATEGORY. THIS ROUNDS OUT THE LOADOUT.",
      "FEHLENDE KATEGORIE. DAS RUNDET DAS LOADOUT AB.",
    ],
    [
      "FRESH COVERAGE — YOU DON'T RUN THIS TYPE YET.",
      "FRISCHE ABDECKUNG — DIESEN TYP HAST DU NOCH NICHT.",
    ],
  ]),
  "over-stacked": quips("comms.swap.overStacked", [
    [
      "YOU'RE STACKED ON THIS TYPE. COVERAGE BEATS MORE OF THE SAME.",
      "DIESER TYP IST SCHON SATT. ABDECKUNG SCHLÄGT MEHR VOM GLEICHEN.",
    ],
    [
      "DECK'S HEAVY HERE ALREADY. DIMINISHING RETURNS.",
      "DEIN DECK IST HIER SCHON SCHWER. ABNEHMENDER NUTZEN.",
    ],
    [
      "MORE OF WHAT YOU HAVE — ONLY IF A SLOT IS DEAD WEIGHT.",
      "MEHR VOM GLEICHEN — NUR WENN EIN SLOT TOTLAST IST.",
    ],
    [
      "SAME LANE AGAIN. SPREAD THE COVERAGE INSTEAD.",
      "WIEDER DIESELBE BAHN. VERTEIL LIEBER DIE ABDECKUNG.",
    ],
  ]),
  hold: quips("comms.swap.hold", [
    [
      "SOLID DECK ALREADY — ONLY SWAP IF IT BEATS YOUR WEAKEST.",
      "SCHON SOLIDES DECK — TAUSCH NUR, WENN ES DEINEN SCHWÄCHSTEN SCHLÄGT.",
    ],
    [
      "DOESN'T TOP ANY SLOT YOU HOLD. KEEPING IS FINE.",
      "ÜBERTRIFFT KEINEN DEINER SLOTS. BEHALTEN IST OKAY.",
    ],
    [
      "YOUR FIVE ARE STRONGER. NO SHAME IN HOLDING.",
      "DEINE FÜNF SIND STÄRKER. BEHALTEN IST KEINE SCHANDE.",
    ],
    [
      "MARGINAL. HOLD UNLESS A SLOT'S ALREADY SPENT.",
      "GRENZWERTIG. BEHALTEN, AUSSER EIN SLOT IST VERBRAUCHT.",
    ],
  ]),
});

/** EN/DE corpus for the commander quips + swap advice (BL-89). */
export { COMMS_QUIP_EN, COMMS_QUIP_DE };

/** Each case's matching VERA pose (owner CR — face agrees with the line).
 *  `cheer` = go-for-it · `alert` = think-about-it · `idle` = neutral.
 *  `wow` is intentionally absent (kernel_panic-exclusive). */
export const SWAP_CASE_POSE: Record<SwapCase, VeraPose> = {
  legendary: "cheer",
  "fills-a-gap": "cheer",
  "clear-upgrade": "cheer",
  "over-stacked": "alert",
  hold: "alert",
  generic: "idle",
};

/**
 * Classify the swap situation from objective facts (rarity + category coverage +
 * weakest held slot). Pure: no RNG, no sim. First match wins, in this priority:
 *   1. legendary     — the contender is legendary (always a special).
 *   2. fills-a-gap    — the contender's category is absent from your deck.
 *   3. clear-upgrade  — the contender outranks your weakest held slot.
 *   4. over-stacked   — its category is already heavy (≥3) and it's no upgrade.
 *   5. hold           — it tops nothing you hold (≤ your weakest).
 *   6. generic        — fallback (e.g. an empty loadout in a preview).
 */
export function swapSituation(contender: Card, loadout: Card[]): SwapCase {
  const held = loadout.filter(Boolean);
  if (contender.rarity === "legendary") return "legendary";
  if (held.length === 0) return "generic";

  const catCount = held.filter((c) => c.category === contender.category).length;
  if (catCount === 0) return "fills-a-gap";

  const ranks = held.map((c) => RARITY_RANK[c.rarity] ?? 0);
  const weakest = Math.min(...ranks);
  const cRank = RARITY_RANK[contender.rarity] ?? 0;

  if (cRank > weakest) return "clear-upgrade";
  if (catCount >= 3) return "over-stacked";
  return "hold";
}

export interface SwapAdvice {
  /** The recognised situation. */
  case: SwapCase;
  /** The chosen VERA line (no immediate repeat vs `opts.avoid`). */
  line: string;
  /** The matching VERA pose (`setMascotPose` drives the face). */
  pose: VeraPose;
}

/**
 * The contextual swap advisor — read-only. Classifies the situation, then picks a
 * line from that case's pool avoiding an immediate repeat (`opts.avoid` = the last
 * line shown). `generic` draws from the rarity pools. Render-only RNG (defaults to
 * `Math.random`), so it never touches the sim.
 */
export function swapAdvice(
  contender: Card,
  loadout: Card[],
  opts: { rng?: () => number; avoid?: string } = {},
): SwapAdvice {
  const rng = opts.rng ?? Math.random;
  const which = swapSituation(contender, loadout);
  const basePool =
    which === "generic"
      ? COMMANDER_QUIPS[contender.rarity] ?? COMMANDER_QUIPS.common
      : SWAP_ADVICE[which];

  // No-immediate-repeat: drop the last line when the pool can still vary.
  const pool =
    opts.avoid && basePool.length > 1
      ? basePool.filter((l) => l !== opts.avoid)
      : basePool;
  const usable = pool.length > 0 ? pool : basePool;

  const idx = Math.min(usable.length - 1, Math.floor(rng() * usable.length));
  return {
    case: which,
    line: usable[Math.max(0, idx)] ?? usable[0] ?? "",
    pose: SWAP_CASE_POSE[which],
  };
}
